import React, { useMemo } from "react";
import { DateTime } from "luxon";

import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";

import GanttSettimana from "./GanttSettimana";

// ============================================
// INTERFACES
// ============================================

interface GanttHeaderProps {
    dataDa: DateTime;
    numeroSettimane: 1 | 2 | 3 | 4 | 6;
}

// ============================================
// UTILITIES
// ============================================

/**
 * Genera array di settimane, ogni settimana contiene 6 giorni (lun-sab)
 */
const generaSettimane = (dataDa: DateTime, numeroSettimane: number): DateTime[][] => {
    const settimane: DateTime[][] = [];

    // Trova il lunedì della settimana di partenza
    let lunedi = dataDa.startOf("week");

    for (let s = 0; s < numeroSettimane; s++) {
        const giorni: DateTime[] = [];

        // 6 giorni: lun-sab (weekday 1-6 in Luxon)
        for (let d = 0; d < 6; d++) {
            giorni.push(lunedi.plus({ days: d }));
        }

        settimane.push(giorni);
        lunedi = lunedi.plus({ weeks: 1 });
    }

    return settimane;
};

/**
 * Calcola lg per settimana in base al numero totale
 */
const getLgSettimana = (numeroSettimane: number): number => {
    switch (numeroSettimane) {
        case 1: return 12;
        case 2: return 6;
        case 3: return 4;
        case 4: return 3;
        case 6: return 2;
        default: return 12;
    }
};

// ============================================
// COMPONENT
// ============================================

const GanttHeader = ({ dataDa, numeroSettimane }: GanttHeaderProps) => {
    const theme = useTheme();

    const settimane = useMemo(
        () => generaSettimane(dataDa, numeroSettimane),
        [dataDa, numeroSettimane]
    );

    const lgSettimana = getLgSettimana(numeroSettimane);

    return (
        <Box
            sx={{
                position: "sticky",
                top: 0,
                zIndex: 10,
                backgroundColor: theme.palette.background.paper,
                borderBottom: `2px solid ${theme.palette.divider}`,
            }}
        >
            <Grid container>
                {/* Colonna macchine (sidebar) */}
                <Grid
                    size={{ xs: 2 }}
                    sx={{
                        backgroundColor: theme.palette.grey[100],
                        borderRight: `1px solid ${theme.palette.divider}`,
                        p: 1,
                    }}
                >
                    <Typography variant="subtitle2" fontWeight="bold">
                        Macchine
                    </Typography>
                </Grid>

                {/* Area settimane */}
                <Grid size={{ xs: 10 }}>
                    <Grid container>
                        {settimane.map((giorni, index) => (
                            <Grid
                                key={index}
                                size={{ xs: lgSettimana }}
                            >
                                <GanttSettimana giorni={giorni} />
                            </Grid>
                        ))}
                    </Grid>
                </Grid>
            </Grid>
        </Box>
    );
};

export default GanttHeader;